import { ProfileTradeMapRegion, ProfileTradeMapResponse, ProfileTradeMapTotals } from 'types/interfaces/profile';
import { regionKey, regionLonLat } from 'components/TradeTabPanel/geo/regionCentroids';

// The data behind every trade-map surface: the profile map (desktop and
// phone), the stat tiles and the share PNG. Pure: no React, no d3, no DOM.
//
// Source: the PUBLIC aggregate GET /public/profile/:username/trade-map, the one
// endpoint every surface reads (addendum 14), so the sidebar's Trade Partners,
// the tile, the card and the crawler unfurl can never disagree.
//
// TWO RULES, both learned the hard way on the mock (Asana 1218719319867092):
//
// 1. The drawing never redefines the data. `totals` is the aggregate's object
//    VERBATIM; nothing is recounted from the rows. Partners are derived from the
//    rows afterwards. A row that cannot be placed still counts wherever the
//    backend counted it.
//
// 2. A public post must not name the people you trade with. The aggregate
//    carries no username and no id, the model has none, and a test pins it.

/** How fine a location the map may show. 'state' matches the in-product map (ruling 2026-09-21). */
export const MAP_LOCATION_GRANULARITY: 'state' | 'country' = 'state';

export interface TradeMapPartner {
  /** regionKey plus an ordinal: stable across renders, never an id */
  key: string;
  region: ProfileTradeMapRegion;
  lon: number;
  lat: number;
  /** Completed packages this partner sent the owner */
  received: number;
  /** Completed packages the owner sent this partner */
  sent: number;
  /** Packages in the mail from this partner to the owner */
  liveInbound: number;
  /** Packages in the mail from the owner to this partner */
  liveOutbound: number;
}

export interface TradeMapModel {
  totals: ProfileTradeMapTotals;
  /** Rows with no placeable region, so no marker */
  unplaced: number;
  /** Distinct ISO-2 codes of countries with a completed trade, for the base-map highlight */
  tradedCountries: string[];
  home: { lon: number; lat: number; region: ProfileTradeMapRegion } | null;
  /** Placeable rows, busiest first. Includes in-transit-only partners (zero completed counts). */
  partners: TradeMapPartner[];
}

const mapRegion = (region: ProfileTradeMapRegion | null | undefined): ProfileTradeMapRegion => ({
  country: region?.country?.trim().toUpperCase() ?? null,
  state: MAP_LOCATION_GRANULARITY === 'state' ? region?.state?.trim().toUpperCase() ?? null : null,
});

export const buildTradeMapModel = (response: ProfileTradeMapResponse): TradeMapModel => {
  const partners: TradeMapPartner[] = [];
  const traded = new Set<string>();
  const ordinals = new Map<string, number>();
  let unplaced = 0;

  response.partners.forEach((row) => {
    const region = mapRegion(row.region);
    if (row.receivedCount + row.sentCount > 0 && region.country) traded.add(region.country);
    const key = regionKey(region);
    const lonLat = key ? regionLonLat(region) : null;
    if (!key || !lonLat) {
      unplaced += 1;
      return;
    }
    const ordinal = ordinals.get(key) ?? 0;
    ordinals.set(key, ordinal + 1);
    partners.push({
      key: `${key}#${ordinal}`,
      region,
      lon: lonLat[0],
      lat: lonLat[1],
      received: row.receivedCount,
      sent: row.sentCount,
      liveInbound: row.inTransitInbound,
      liveOutbound: row.inTransitOutbound,
    });
  });

  const homeRegion = mapRegion(response.home);
  const homeLonLat = regionLonLat(homeRegion);

  return {
    totals: { ...response.totals },
    unplaced,
    tradedCountries: Array.from(traded).sort(),
    home: homeLonLat ? { lon: homeLonLat[0], lat: homeLonLat[1], region: homeRegion } : null,
    // Busiest first, so layout, draw order and the animation cap are deterministic
    partners: partners.sort(
      (a, b) =>
        b.received + b.sent - (a.received + a.sent) ||
        b.liveInbound + b.liveOutbound - (a.liveInbound + a.liveOutbound) ||
        a.lon - b.lon ||
        a.lat - b.lat
    ),
  };
};

/** True when nothing would be drawn: the caller renders no map block (addendum 15) */
export const isEmptyTradeMap = (model: TradeMapModel): boolean =>
  model.totals.received + model.totals.sent + model.totals.inTransit === 0;
